<template>
  <CModal
    v-if="property"
    :show="show"
    :close-on-backdrop="false"
    title="Edit Posting Details"
    @update:show="close"
  >
    <CForm>
      <CRow>
        <CCol>
          <div>
            <h6>Custom Titles</h6>
            <CSelect
              :value.sync="formData.title_setting"
              label="Behavior"
              :options="getTitleOptions()"
            />
            <RListInput
              ref="titles"
              :items="formData.title"
              label="Titles"
              invalid-feedback="This field cannot exceed 73 characters"
              :max-length="73"
            />
          </div>
          <div class="pt-4">
            <h6>Ad Body</h6>
            <RListInput
              ref="romanceText"
              :items="formData.romance_text"
              label="Romance Text"
              invalid-feedback="This field cannot exceed 5000 characters"
              :rows="2"
              :max-length="500"
            />
          </div>
          <div class="pt-4">
            <h6>Banners & Specials</h6>
            <RListInput
              ref="specials"
              :items="formData.specials"
              label="Specials"
              invalid-feedback="This field cannot exceed 1000 characters"
              :max-length="1000"
            />
            <RListInput
              ref="header"
              :items="formData.header"
              label="Header(s)"
              invalid-feedback="This field cannot exceed 1000 characters"
              :max-length="1000"
            />
            <RListInput
              ref="footer"
              :items="formData.footer"
              label="Footer(s)"
              invalid-feedback="This field cannot exceed 1000 characters"
              :max-length="1000"
            />
            <RListInput
              ref="disclaimer"
              :items="formData.disclaimer"
              label="Disclaimer(s)"
              invalid-feedback="This field cannot exceed 1000 characters"
              :max-length="1000"
            />
          </div>
          <div class="pt-4">
            <h6>Amenities</h6>
            <RListInput
              ref="amenitiesProperty"
              :items="formData.amenities_property"
              label="Amenities (property)"
              invalid-feedback="This field cannot exceed 1000 characters"
              :max-length="1000"
            />
            <RListInput
              ref="amenitiesUnit"
              :items="formData.amenities_unit"
              label="Amenities (unit)"
              invalid-feedback="This field cannot exceed 1000 characters"
              :max-length="1000"
            />
            <CSelect
              :value.sync="formData.furnished"
              label="Furnished"
              :options="getPolicyOptions()"
            />
            <CSelect
              :value.sync="formData.dog_policy"
              label="Dog Policy"
              :options="getPolicyOptions()"
            />
            <CSelect
              :value.sync="formData.cat_policy"
              label="Cat Policy"
              :options="getPolicyOptions()"
            />
            <CSelect
              :value.sync="formData.parking"
              label="Parking"
              :options="getParkingOptions()"
            />
            <CSelect
              :value.sync="formData.ev_charging"
              label="EV Charging"
              :options="getPolicyOptions()"
            />
            <CSelect
              :value.sync="formData.air_conditioning"
              label="Air Conditioning"
              :options="getPolicyOptions()"
            />
            <CSelect
              :value.sync="formData.laundry"
              label="Laundry"
              :options="getLaundryOptions()"
            />
            <CSelect
              :value.sync="formData.smoking_policy"
              label="Smoking Policy"
              :options="getSmokingPolicyOptions()"
            />
            <CSelect
              :value.sync="formData.wheelchair_access"
              label="Wheelchair Accessible"
              :options="getPolicyOptions()"
            />
          </div>
        </CCol>
      </CRow>
    </CForm>
    <template #footer>
      <CButton class="btn-close" variant="outline" @click="close">
        Cancel
      </CButton>
      <CButton color="primary" @click="save">
        Save
      </CButton>
    </template>
  </CModal>
</template>

<script>
import RListInput from '@/components/inputs/RListInput'
import { CraigslistAPI } from '@/services/api/resources'
import { deepCopy } from '@/utils'

export default {
  name: 'PostingDetailEdit',
  components: {
    RListInput
  },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    property: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      formData: {
        title_setting: '',
        title: [],
        romance_text: [],
        specials: [],
        disclaimer: [],
        header: [],
        footer: [],
        amenities_property: [],
        amenities_unit: [],
        furnished: false,
        dog_policy: false,
        cat_policy: false,
        parking: '',
        ev_charging: false,
        air_conditioning: false,
        laundry: '',
        smoking_policy: false,
        wheelchair_access: false
      }
    }
  },
  watch: {
    property (value) {
      this.formData = deepCopy(value)
    }
  },
  created () {
    this.formData = deepCopy(this.property)
  },
  methods: {
    /**
     * Validate the form
     */
    isValid () {
      return this.$refs['titles'].validate() &&
        this.$refs['romanceText'].validate() &&
        this.$refs['specials'].validate() &&
        this.$refs['header'].validate() &&
        this.$refs['footer'].validate() &&
        this.$refs['disclaimer'].validate() &&
        this.$refs['amenitiesUnit'].validate() &&
        this.$refs['amenitiesProperty'].validate()
    },
    /**
     * Submits form to update posting details
     */
    async save () {
      if (!this.isValid()) {
        return
      }
      const propertyConfig = { id: this.$route.params.pid, data: deepCopy(this.formData) }
      try {
        await CraigslistAPI.properties.portalUpdate(propertyConfig)
        this.$store.commit('notification/show', {
          message: 'Posting details updated',
          type: 'success'
        })
        this.$emit('update:property')
      } catch (err) {
        this.$store.dispatch('error/show', err)
      } finally {
        this.close()
      }
    },
    /**
     * Resets validation and dispatches close action.
     */
    close () {
      this.$refs['titles'].reset()
      this.$refs['romanceText'].reset()
      this.$refs['specials'].reset()
      this.$refs['header'].reset()
      this.$refs['footer'].reset()
      this.$refs['disclaimer'].reset()
      this.$refs['amenitiesUnit'].reset()
      this.$refs['amenitiesProperty'].reset()
      this.$emit('update:show', false)
    },
    /**
     * Get parking choices to be displayed.
     *
     * @return {Array}
     */
    getParkingOptions () {
      return [
        { label: 'Unknown', value: '' },
        { label: 'Carport', value: 'carport' },
        { label: 'Attached Garage', value: 'attached-garage' },
        { label: 'Detached Garage', value: 'detached-garage' },
        { label: 'Off-Street', value: 'off-street' },
        { label: 'Street', value: 'street' },
        { label: 'Valet', value: 'valet' },
        { label: 'None', value: 'none' }
      ]
    },
    /**
     * Get laundry choices to be displayed.
     *
     * @return {Array}
     */
    getLaundryOptions () {
      return [
        { label: 'Unknown', value: '' },
        { label: 'In Unit', value: 'w/d-in-unit' },
        { label: 'Hookups', value: 'w/d-hookups' },
        { label: 'In Building', value: 'laundry-in-building' },
        { label: 'On Site', value: 'laundry-on-site' },
        { label: 'None', value: 'none' }
      ]
    },
    /**
     * Get smoking policy choices to be displayed.
     *
     * @return {Array}
     */
    getSmokingPolicyOptions () {
      return [
        { label: 'Unknown', value: null },
        { label: 'No Smoking', value: true },
        { label: 'Smoking Ok', value: false }
      ]
    },
    /**
     * Get policy choices to be displayed.
     *
     * @return {Array}
     */
    getPolicyOptions () {
      return [
        { label: 'Unknown', value: null },
        { label: 'Yes', value: true },
        { label: 'No', value: false }
      ]
    },
    /**
     * Get custom title setting choices to be displayed.
     *
     * @return {Array}
     */
    getTitleOptions () {
      return [
        { label: 'Let PostEngine auto-generate titles', value: 'no_titles' },
        { label: 'Randomly pick between stored and auto-generated titles', value: 'alternate_titles' },
        { label: 'Randomly pick from stored titles only', value: 'stored_only' }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>

::v-deep label {
  font-size: 0.8em;
}
::v-deep a {
  font-size: 0.8em;
}
.btn-primary {
  background-color: $primary-base;
}
.btn-close {
  background-color: $gray-50;
}
</style>
